import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { checkLockStatus } from '../../utils/services/complexDocumentsAPI';
import DeleteTemplateModal from '../../components/Modals/DeleteTemplateModal';
import TemplateLockedModal from '../ComplexDocumentDesign/TemplateLockedModal';
import PageNotFoundIllustration from '../../images/generatingPreview.png';
import moment from 'moment';
import { Dropdown, Menu, Icon, Tooltip, message, Popover } from 'antd';
import { GlobeIcon } from '../../images/svgIcons';
import { NEW_EMAIL_TEMPLATE_SECTION } from './translationValues';
import URL from '../../utils/helper';
import { languageCountryMap } from '../NewTemplateSection/languageCountryMap';
import {
  Card,
  PreviewSection,
  PreviewImage,
  GradientOverlay,
  LanguageBadge,
  TopActions,
  ActionButton,
  BottomActions,
  PageDate,
  InfoSection,
  CardTitle,
  CategoryBadge,
  LanguageSelector,
  LanguageButton,
  SingleLanguageBadge,
  LanguagePopover,
} from './../NewTemplateSection/styles';


const OverflowTooltip = ({ children, title }) => {
  const [truncated, setTruncated] = useState(false);

  const handleMouseEnter = e => {
    if (e.target.scrollWidth > e.target.clientWidth) {
      setTruncated(true);
    } else {
      setTruncated(false);
    }
  };

  return (
    <Tooltip title={truncated ? title : ''}>
      {React.cloneElement(children, {
        onMouseEnter: e => {
          handleMouseEnter(e);
          if (children.props.onMouseEnter) children.props.onMouseEnter(e);
        },
      })}
    </Tooltip>
  );
};

const THUMBNAIL_KEY = 'thumbnail';

const generateThumbnailUrl = (id, selectedView) => {
  return selectedView === 'emails'
    ? `${URL.DESIGN_SYSTEM_URL}email/thumbnail/${id}`
    : `${URL.DESIGN_SYSTEM_URL}email/standalone/block/thumbnail/${id}`;
};

export function EmailCard({
  selectedView,
  categoryList,
  template,
  highlightLanguage,
  setRefreshPage,
  allLanguages,
}) {
  const { t } = useTranslation(['NEW_EMAIL_TEMPLATE_SECTION']);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    highlightLanguage || template?.templates?.[0]?.language,
  );

  const [lockedBy, setLockedBy] = useState(null);
  const [modalVisibility, setModalVisibility] = useState(null);
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);
  const [firstPageImageLink, setFirstPageImageLink] = useState(
    template?.templates?.[0]?.[THUMBNAIL_KEY] || undefined
  );

  const { userDetails, checkUserAccessRoles } = useContext(AppStoreContext);

  const history = useHistory();

  useEffect(() => {
    const thumbnail = currentVariant?.[THUMBNAIL_KEY];
    if (!thumbnail) {
      setFirstPageImageLink(
        generateThumbnailUrl(
          currentVariant?.templateId,
          selectedView,
        ),
      );
    }
  }, [template, selectedLanguage]);

  const sortedLanguages = React.useMemo(() => {
    if (!template?.templates) return [];
    const validTemplates = template.templates.filter(v => v.language);

    return [...validTemplates].sort((a, b) => {
      if (a.language === highlightLanguage) return -1;
      if (b.language === highlightLanguage) return 1;
      return 0;
    });
  }, [template?.templates, highlightLanguage]);

  const visibleLanguages = React.useMemo(() => {
    if (sortedLanguages.length <= 3) return sortedLanguages;

    const topThree = sortedLanguages.slice(0, 3);
    const isSelectedInTopThree = topThree.some(t => t.language === selectedLanguage);

    if (isSelectedInTopThree) {
      return topThree;
    }

    const selectedVariant = sortedLanguages.find(t => t.language === selectedLanguage);
    if (selectedVariant) {
      return [topThree[0], topThree[1], selectedVariant];
    }

    return topThree;
  }, [sortedLanguages, selectedLanguage]);

  const languageImageError = (e) => {
    e.target.style.display = "none";
    e.target.insertAdjacentText("afterend", "🌐");
  }

  const LanguagePopoverContent = useCallback(({ t }) => (
    <LanguagePopover>
      <div className="popover_header">
        {sortedLanguages.length}{' '}
        {t(
          NEW_EMAIL_TEMPLATE_SECTION.LANGUAGES_AVAILABLE.key,
          NEW_EMAIL_TEMPLATE_SECTION.LANGUAGES_AVAILABLE.value,
        )}
      </div>
      <div className="popover_body">
        {sortedLanguages.map(variant => (
          <LanguageButton
            key={variant.language}
            selected={selectedLanguage === variant.language}
            highlighted={highlightLanguage === variant.language}
            onClick={() => {
              setSelectedLanguage(variant.language);
              setShowLanguagePopup(false);
            }}
          >
            <img src={`https://flagsapi.com/${languageCountryMap[variant?.language]}/shiny/16.png`} 
              alt="" 
              onError={languageImageError}
            />
            <span>
              {allLanguages?.find(ln => ln.code === variant.language)?.name || variant.language}
            </span>
          </LanguageButton>
        ))}
      </div>
    </LanguagePopover>
  ), [sortedLanguages, selectedLanguage]);

  const redirectToTemplate = id => {
    if (selectedView === 'emails') {
      history.push(`/complexEmailDesign/${id}`);
    } else {
      history.push(`/complexEmailBlock/${id}`);
    }
  };

  const currentVariant =
    template?.templates?.find(v => v.language === selectedLanguage) || template?.templates?.[0];

  const categoryName = categoryList.find(category => category.categoryId === template.categoryId)
    ?.categoryName;

  const templateId =
    currentVariant?.templateId;

  const templateName =
    template.communicationName || currentVariant?.templateName;

  const handleTemplateDeletion = async () => {
    let payload = {
      resourceId: currentVariant?.templateId,
      resourceType:
        selectedView === 'emails'
          ? 'email'
          : 'block',
    };
    const templateStatus = await checkLockStatus(payload);
    if (templateStatus.statusCode === 741) {
      if (userDetails.id !== templateStatus.data.userId) {
        setLockedBy(templateStatus.data.userName);
        setModalVisibility('locked');
        return;
      }
    }
    setModalVisibility('delete');
  };

  const onImageError = useCallback(() => {
    setFirstPageImageLink(undefined);
  }, []);

  return (
    <>
      <Card onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {/* Preview */}
        <PreviewSection onClick={() => redirectToTemplate(templateId)}>
          <PreviewImage
            src={
              currentVariant?.[THUMBNAIL_KEY] ||
              firstPageImageLink ||
              PageNotFoundIllustration
            }
            onError={onImageError}
            alt={templateName}
          />

          {/* Gradient Overlay */}
          <GradientOverlay />

          {/* Language Badge - Top Left */}
          {template?.templates?.length > 1 && (
            <LanguageBadge>
              <GlobeIcon />
              <span>
                {template?.templates?.length}{' '}
                {t(
                  NEW_EMAIL_TEMPLATE_SECTION.LANGUAGES_COUNT.key,
                  NEW_EMAIL_TEMPLATE_SECTION.LANGUAGES_COUNT.value,
                )}
              </span>
            </LanguageBadge>
          )}

          {/* Top Actions */}
          <TopActions visible={isHovered}>
            <ActionButton>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item onClick={() => redirectToTemplate(templateId)}>
                      {t(NEW_EMAIL_TEMPLATE_SECTION.EDIT.key, NEW_EMAIL_TEMPLATE_SECTION.EDIT.value)}
                    </Menu.Item>
                    {(selectedView === 'emails' ||
                      (['reusable_sections'].includes(selectedView) &&
                        checkUserAccessRoles(['REUSABLECONTENTMANAGER']))) && (
                        <Menu.Item
                          onClick={e => {
                            e.domEvent.stopPropagation();
                            handleTemplateDeletion();
                          }}
                        >
                          {t(NEW_EMAIL_TEMPLATE_SECTION.DELETE.key, NEW_EMAIL_TEMPLATE_SECTION.DELETE.value)}
                        </Menu.Item>
                      )}
                  </Menu>
                }
                trigger={'click'}
              >
                <Icon type="ellipsis" size={16} onClick={e => e.stopPropagation()} />
              </Dropdown>
            </ActionButton>
          </TopActions>

          {/* Bottom Actions */}
          <BottomActions>
            {/* Page Count Badge */}
            <PageDate className="updatedon_date">
              <p>{moment(currentVariant?.updatedOn).format('D')}</p>
              <span>{moment(currentVariant?.updatedOn).format('MMM')}</span>
            </PageDate>
          </BottomActions>
        </PreviewSection>

        {/* Info */}
        <InfoSection>
          <OverflowTooltip title={templateName}>
            <CardTitle>{templateName}</CardTitle>
          </OverflowTooltip>

          <div
            className="badge_wrapper"
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            {categoryName && (
              <OverflowTooltip title={categoryName}>
                <CategoryBadge>{categoryName}</CategoryBadge>
              </OverflowTooltip>
            )}

            {/* Language Selector */}
            {template?.templates?.length > 1 ? (
              <LanguageSelector>
                {visibleLanguages.map(
                  variant =>
                    variant.language && (
                      <Tooltip
                        key={variant.language}
                        title={allLanguages?.find(ln => ln.code === variant.language)?.name}
                      >
                        <LanguageButton
                          selected={selectedLanguage === variant.language}
                          highlighted={highlightLanguage === variant.language}
                          onClick={() => setSelectedLanguage(variant.language)}
                        >
                          <img src={`https://flagsapi.com/${languageCountryMap[variant?.language]}/shiny/16.png`}
                            alt="" 
                            onError={languageImageError}
                          />
                          <span>{variant.language}</span>
                        </LanguageButton>
                      </Tooltip>
                    ),
                )}
                {sortedLanguages.length > 3 && (
                  <Popover
                    content={<LanguagePopoverContent t={t} />}
                    trigger="click"
                    visible={showLanguagePopup}
                    onVisibleChange={setShowLanguagePopup}
                    placement="bottomRight"
                    overlayClassName="language-popover"
                  >
                    <Tooltip
                      title={t(
                        NEW_EMAIL_TEMPLATE_SECTION.VIEW_ALL_LANGUAGES_TOOLTIP.key,
                        NEW_EMAIL_TEMPLATE_SECTION.VIEW_ALL_LANGUAGES_TOOLTIP.value,
                      )}
                    >
                      <LanguageButton
                        onClick={e => {
                          e.stopPropagation();
                          // Toggle handled by Popover
                        }}
                        style={{ background: '#f3f4f6', color: '#4b5563' }}
                      >
                        <span>+{sortedLanguages.length - 3}</span>
                      </LanguageButton>
                    </Tooltip>
                  </Popover>
                )}
              </LanguageSelector>
            ) : (
              (template?.templates?.[0]?.language) && (
                <Tooltip
                  title={
                    allLanguages?.find(
                      ln => ln.code === (template?.templates?.[0]?.language),
                    )?.name
                  }
                >
                  <SingleLanguageBadge>
                    <img src={`https://flagsapi.com/${languageCountryMap[template?.templates?.[0]?.language]}/flat/16.png`}
                      alt="" 
                      onError={languageImageError}
                    />
                    {template?.templates?.[0]?.language}
                  </SingleLanguageBadge>
                </Tooltip>
              )
            )}
          </div>
        </InfoSection>
      </Card>
      <DeleteTemplateModal
        visible={modalVisibility === 'delete'}
        onCancel={() => setModalVisibility(null)}
        targetTemplate={
          {
            ...currentVariant,
            id: currentVariant?.templateId,
            name: templateName,
          }
        }
        categoryType={selectedView === 'reusable_sections' ? 'element' : 'template'}
        onSucess={() => {
          message.success(
            t(
              NEW_EMAIL_TEMPLATE_SECTION.TEMPLATE_DELETED.key,
              NEW_EMAIL_TEMPLATE_SECTION.TEMPLATE_DELETED.value,
            ),
          );
          setModalVisibility(null);
          setRefreshPage(prev => prev + 1);
        }}
        isElement={selectedView === 'reusable_sections'}
        isMailTemplate={selectedView === 'emails'}
        complexEmailTemplate={true}
        activeContent={selectedView === 'reusable_sections' ? 'Referenced' : ''}
      />
      <TemplateLockedModal
        visible={modalVisibility === 'locked'}
        onClose={() => setModalVisibility(null)}
        lockedByUser={lockedBy}
        deletion={true}
      />
    </>
  );
}
